import { Box, Flex } from '@iheartjane/reefer';
import { Suspense, lazy } from 'react';

import { LoadingWrapper } from '@jane/shared/components';

import { selectIdentification } from '../../customer/redux/identification';
import { useCustomerSelector } from '../../customer/selectors';
import type { IdentificationProps } from './identification.types';
import styles from './mmjIdentification.module.css';
import { WarningMessage } from './warningMessage';

/** Code splitting to load convertIfPDF/pdfjs only when called */
const IdentificationUploader = lazy(() =>
  import('./idUploader').then(({ IdentificationUploader }) => ({
    default: IdentificationUploader,
  }))
);

const uploaderLabel = (customLanguage?: string | null, side?: string): string =>
  `Upload ${customLanguage || `${side} of medical ID card`}`;

export const MMJIdentification = ({
  showValidationErrors,
  valid,
  onIdUploadAttempt,
  onRemove,
  cardFrontComplianceLanguage,
  cardBackComplianceLanguage,
}: IdentificationProps) => {
  const { document, loading_mmj_card, loading_mmj_card_back } =
    useCustomerSelector(selectIdentification);

  return (
    <Suspense fallback={<LoadingWrapper isLoading />}>
      <Flex gap={24} className={styles['mmjIdentification']}>
        <IdentificationUploader
          document={document}
          label={uploaderLabel(cardFrontComplianceLanguage, 'front')}
          imageKey="mmj_card"
          inputName="mmj-card"
          isLoading={loading_mmj_card}
          valid={valid}
          showValidationErrors={showValidationErrors}
          onIdUploadAttempt={onIdUploadAttempt}
          onRemove={onRemove}
        />

        <IdentificationUploader
          document={document}
          imageKey="mmj_card_back"
          inputName="mmj-card-back"
          label={uploaderLabel(cardBackComplianceLanguage, 'back')}
          isLoading={loading_mmj_card_back}
          valid={valid}
          showValidationErrors={showValidationErrors}
          onIdUploadAttempt={onIdUploadAttempt}
          onRemove={onRemove}
        />
      </Flex>

      {(!document.mmj_card || !document.mmj_card_back) && (
        <Box mt={8} data-testid="mmj-id-warning">
          <WarningMessage>ID required for discount</WarningMessage>
        </Box>
      )}
    </Suspense>
  );
};
